import * as signalR from "@microsoft/signalr";
import { SetupInstance } from '../../app/msal-account-helpers';
import { useLocal } from './../environment';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { HeartBeatApi } from './heartbeat-api';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class SignalRApi {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;

        // Do not pin to accounts[0]; rely on MSAL active account
        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.signalR.scopes.use
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            // account is selected by AccessTokenHelper via instance.getActiveAccount()
        };

        this.baseUrl = useLocal ?
            "http://localhost:8084/api/signalr" :
            "https://apim.ianrobertson.co.uk/api/signalr/v1";

        this.accessTokenService = new AccessTokenHelper();

        this.subscribers = {};
        this.handlersRegistered = {};

        this.state = "Not Started";

        this.heartBeatApi = new HeartBeatApi(instance, inProgress, accounts, forceRefresh);
    }

    // Ensure a SignalR handler is registered for a given message type
    ensureHandler = (messageType) => {
        if (!this.connection) {
            return;
        }

        if (this.handlersRegistered[messageType]) {
            return;
        }

        this.connection.on(messageType, message => {

            const result = Array.isArray(message) ?
                cleanUpJsonForTransport(Object.assign([], hydrateObjects(message))) :
                cleanUpJsonForTransport(Object.assign({}, hydrateObjects(message)));

            if (this.subscribers[messageType]) {
                this.subscribers[messageType].forEach(cb => cb(result));
            }
        });

        this.handlersRegistered[messageType] = true;
    };

    connect = () => {
        return polly()
            .logger(error => {
                console.error("An error occurred in connect", error);
                // InteractionRequiredAuthError is handled centrally by AccessTokenHelper/AuthGate
            })
            .waitAndRetry(10)
            .executeForPromise(async () => {
                try {
                    // Reset any existing connection
                    if (this.connection) {
                        await this.connection.stop();
                        this.connection = null;
                    }

                    this.state = "Starting";

                    const tokenProvider = async () => {
                        const tokenResponse = await this.accessTokenService.getAccessToken(
                            this.instance,
                            this.inProgress,
                            this.accessTokenRequest
                        );

                        if (!tokenResponse) {
                            throw new Error("No token received");
                        }

                        // Rely on MSAL's internal caching/refresh via acquireTokenSilent
                        return tokenResponse.accessToken;
                    };

                    // Get initial token to set up headers
                    const initialTokenResponse = await this.accessTokenService.getAccessToken(
                        this.instance,
                        this.inProgress,
                        this.accessTokenRequest
                    );

                    const options = {
                        accessTokenFactory: tokenProvider,
                        headers: {
                            ...(initialTokenResponse.idTokenClaims?.oid ? 
                                {'x-ms-signalr-userid': initialTokenResponse.idTokenClaims.oid} : 
                                {})
                        }
                    };

                    // Don't proceed with connection if idTokenClaims or oid are falsey
                    if (!initialTokenResponse.idTokenClaims?.oid) {
                        console.error('Cannot establish SignalR connection: missing user ID in token claims');
                        this.state = "Error";
                        throw new Error('Missing user ID in token claims');
                    }
                    this.connection = new signalR.HubConnectionBuilder()
                        .withUrl(this.baseUrl, options)
                        .configureLogging(signalR.LogLevel.Error)
                        .withAutomaticReconnect({
                            nextRetryDelayInMilliseconds: retryContext => {
                                if (retryContext.previousRetryCount === 0) {
                                    return 0;
                                }
                                if (retryContext.elapsedMilliseconds < 60000) {
                                    return 2000;
                                }
                                if (retryContext.elapsedMilliseconds < 300000) {
                                    return 30000;
                                }
                                return 60000;
                            }
                        })
                        .build();

                    // Register handlers for any message types that already have subscribers
                    this.handlersRegistered = {};
                    Object.keys(this.subscribers).forEach(messageType => this.ensureHandler(messageType));

                    // Add connection lifecycle event handlers
                    this.connection.onreconnecting(error => {
                        console.warn('SignalR reconnecting:', error);
                        this.state = "Reconnecting";
                    });

                    this.connection.onreconnected(connectionId => {
                        console.log('SignalR reconnected:', connectionId);
                        this.state = "Connected";
                    });

                    this.connection.onclose(error => {
                        console.error('SignalR connection closed:', error);
                        this.state = "Disconnected";
                    });

                    return true;

                } catch (error) {
                    console.error('Connection error:', error);
                    this.state = "Error";

                    throw error;
                }
            });
    };

    addToGroup = connectionId => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;
        
        return polly()
            .logger(error => {
                console.error("An error occurred in addToGroup", error); // will be hit 5 times if issues
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");
                            headers.append("signalr-connectionId", connectionId);

                            const options = {
                                method: "POST",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}/AddToGroup`, options)
                                .then(response => resolve(response.ok))
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });

    };

    async start() {
        console.log("SignalR Starting...");
        if (this.state === "Started" || this.state === "Starting") {
            return this.state;
        }

        this.state = "Starting";
        
        // Create a timeout promise
        const timeoutPromise = new Promise((_, timeoutReject) => {
            setTimeout(() => {
                timeoutReject(new Error('SignalR connection timeout'));
            }, 15000); // 15 seconds timeout
        });
    
        // Create the connection promise
        const connectionPromise = new Promise(async (connResolve, connReject) => {
            try {
                await this.connect();
                await this.connection.start();
                console.log(`SignalR Connected! ConnectionId: ${this.connection.connectionId}`);
                
                const groupData = await this.addToGroup(this.connection.connectionId);
                console.log("Added to Group", groupData);

                this.heartBeatApi.start();
                this.state = "Started";
                console.log("SignalR Started");
                
                connResolve(this.state);
            } catch (err) {
                this.state = "Faulted"; 
                this.heartBeatApi.stop();
                console.error("An error occurred connecting or starting the service", err); 
                connReject(err);
            }
        });
    
        try {
            // Race between the connection and timeout
            const result = await Promise.race([connectionPromise, timeoutPromise]);
            return result;
        } catch (error) {
            if (error.message === 'SignalR connection timeout') {
                console.error('SignalR connection timed out after 15 seconds.');
            }
            throw error;
        }
    };

    subscribe(messageType, callback) {

        // Guard against invalid callbacks to avoid null entries
        if (typeof callback !== 'function') {
            console.warn(`SignalR subscribe called for '${messageType}' with non-function callback:`, callback);
            return;
        }

        if (!this.subscribers[messageType]) {
            this.subscribers[messageType] = [];
        }

        this.subscribers[messageType].push(callback);

        // Safely register a handler if/when a connection exists
        this.ensureHandler(messageType);

        console.log(`Subscribed to ${messageType}. Current subscribers:`, this.subscribers[messageType]);
    }

    unsubscribe(messageType, callback) {
        if (this.subscribers[messageType]) {
            this.subscribers[messageType] = this.subscribers[messageType].filter(cb => cb !== callback);
        }
    }
};